import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  makeStyles,
  Tooltip,
  Box
} from "@material-ui/core";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Settings as AdminIcon,
  Home,
  PieChart,
  BarChart,
  ConfirmationNumber as Ticket,
  Message as MessageSquare,
  People as Users,
  LocalOffer as Tag,
  AccountBalanceWallet as Wallet,
  Event as Calendar,
  Dashboard as Kanban,
  Android as Bot,
  Announcement as Megaphone,
  AttachMoney as DollarSign,
  Business as Building,
  Phone,
  FlashOn as Zap,
  List as ListIcon,
  DeviceHub as Workflow,
  Chat as MessageCircle,
  Web as LandPlot,
  Group,
  Language as Globe,
  Layers,
  Help as HelpCircle,
  ShoppingCart as OrdersIcon,
  Assessment as AnalyticsIcon,
  LocalShipping as LogisticsIcon,
  Link
} from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";
import { useEffect } from "react";

const useStyles = makeStyles((theme) => ({
  menuButton: {
    color: "rgba(255, 255, 255, 0.9)",
    margin: theme.spacing(0, 0.5),
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  menuPaper: {
    maxHeight: "85vh",
    maxWidth: "520px",
    minWidth: "460px",
    width: "auto",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    borderRadius: "12px",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  categoryHeader: {
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : theme.palette.grey[100],
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: "sticky",
    top: 0,
    zIndex: 10,
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    "&:first-child": {
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
    },
  },
  menuItem: {
    padding: theme.spacing(1.5, 2),
    minHeight: "60px",
    display: "flex",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "translateX(4px)",
      transition: "all 0.2s ease",
    },
    transition: "all 0.2s ease",
    "&:last-child": {
      borderBottomLeftRadius: "12px",
      borderBottomRightRadius: "12px",
    },
  },
  menuItemText: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    "& .MuiListItemText-primary": {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& .MuiListItemText-secondary": {
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
      lineHeight: 1.2,
      marginTop: "4px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  iconWrapper: {
    minWidth: "44px",
    width: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
    "& .MuiSvgIcon-root": {
      fontSize: "1.3rem",
    },
  },

  menuContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  menuButtonIcon: {
    fontSize: "1.5rem",
  },
}));

// Hook para usar a lógica de navegação do ActionSearchBar
function useNavigationActions() {
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const { list: helpsList } = useHelps();

  // Plan-based feature states
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [showLandingPages, setShowLandingPages] = useState(false);
  const [showAssistants, setShowAssistants] = useState(false);
  const [showSDR, setShowSDR] = useState(false);
  const [showNapp, setShowNapp] = useState(false);
  const [showAsterisk, setShowAsterisk] = useState(false);
  const [isOnlyApiMessage, setIsOnlyApiMessage] = useState(false);
  const [showWallets] = useState(false);
  const [hasHelps, setHasHelps] = useState(false);

  // Load plan configurations
  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      try {
        if (!user?.companyId) return;

        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);

        if (isMounted && planConfigs?.plan) {
          setShowCampaigns(planConfigs.plan.useCampaigns);
          setShowKanban(planConfigs.plan.useKanban);
          setShowOpenAi(planConfigs.plan.useOpenAi);
          setShowIntegrations(planConfigs.plan.useIntegrations);
          setShowSchedules(planConfigs.plan.useSchedules);
          setShowInternalChat(planConfigs.plan.useInternalChat);
          setShowExternalApi(planConfigs.plan.useExternalApi);
          setShowGroups(planConfigs.plan.useGroups);
          setShowLandingPages(planConfigs.plan.useLandingPages);
          setShowAssistants(planConfigs.plan.useAssistants);
          setShowSDR(planConfigs.plan.useIASDR);
          setShowNapp(planConfigs.plan.useNapp);
          setShowAsterisk(planConfigs.plan.useAsterisk);
          setIsOnlyApiMessage(!!planConfigs.plan.onlyApiMessage);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Erro ao buscar configurações do plano:", err);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [getPlanCompany, user?.companyId]);

  // Check helps availability
  useEffect(() => {
    setHasHelps(helpsList && helpsList.length > 0);
  }, [helpsList]);

  // Organizar ações por categorias
  const getNavigationCategories = () => {
    if (!user) return { dashboard: [], user: [], admin: [] };

    const dashboard = [];
    const userItems = [];
    const admin = [];

    // Dashboard & Gestão
    if (
      (user.profile === "user" && user.showDashboard === "enabled") ||
      user.allowRealTime === "enabled" ||
      user.profile === "admin"
    ) {
      dashboard.push({
        id: "dashboard",
        label: "Dashboard",
        icon: <Home style={{ color: "#2196f3" }} />,
        description: "Painel principal",
        path: "/",
      });
    }

    if ((user.profile === "user" && user.showDashboard === "enabled") || user.profile === "admin") {
      dashboard.push({
        id: "reports",
        label: "Relatórios",
        icon: <PieChart style={{ color: "#009688" }} />,
        description: "Relatórios e análises",
        path: "/reports",
      });
    }

    if ((user.profile === "user" && user.allowRealTime === "enabled") || user.profile === "admin") {
      dashboard.push({
        id: "moments",
        label: "Chats Tempo Real",
        icon: <BarChart style={{ color: "#4caf50" }} />,
        description: "Conversas em tempo real",
        path: "/moments",
      });
    }

    if (user.profile === "admin" && showWallets) {
      dashboard.push({
        id: "wallets",
        label: "Carteiras",
        icon: <Wallet style={{ color: "#4caf50" }} />,
        description: "Carteiras digitais",
        path: "/wallets",
      });
    }

    // Itens de Usuário (sempre disponíveis ou com permissão específica)
    userItems.push({
      id: "tickets",
      label: "Tickets",
      icon: <Ticket style={{ color: "#4caf50" }} />,
      description: "Gerenciar tickets",
      path: "/tickets",
    });

    // Módulo de Pedidos E-commerce
    if (showNapp) {
      userItems.push({
        id: "orders",
        label: "Pedidos E-commerce",
        icon: <OrdersIcon style={{ color: "#ff9800" }} />,
        description: "Gerenciar pedidos de e-commerce",
        path: "/orders",
      });

      userItems.push({
        id: "orders-analytics",
        label: "Analytics E-commerce",
        icon: <AnalyticsIcon style={{ color: "#9c27b0" }} />,
        description: "Relatórios e métricas de vendas",
        path: "/orders/analytics",
      });

      userItems.push({
        id: "logistics",
        label: "Logística",
        icon: <LogisticsIcon style={{ color: "#2196f3" }} />,
        description: "Sistema de rastreamento e entregas",
        path: "/logistics",
      });
    }

    // Módulo Asterisk VoIP
    if (showAsterisk) {
      userItems.push({
        id: "asterisk-connections",
        label: i18n.t("asterisk.connections.title"),
        icon: <Phone style={{ color: "#4caf50" }} />,
        description: i18n.t("asterisk.settings"),
        path: "/asterisk/connections",
      });

      userItems.push({
        id: "asterisk-calls",
        label: i18n.t("asterisk.callHistory.title"),
        icon: <ListIcon style={{ color: "#2196f3" }} />,
        description: i18n.t("asterisk.callHistory.title"),
        path: "/asterisk/calls",
      });
    }

    userItems.push({
      id: "quick-messages",
      label: "Mensagens Rápidas",
      icon: <MessageSquare style={{ color: "#00bcd4" }} />,
      description: "Respostas rápidas",
      path: "/quick-messages",
    });

    if (showKanban) {
      userItems.push({
        id: "kanban",
        label: "Kanban",
        icon: <Kanban style={{ color: "#9c27b0" }} />,
        description: "Quadro Kanban",
        path: "/kanban",
      });
    }

    if (user.showContacts === "enabled") {
      userItems.push({
        id: "contacts",
        label: "Contatos",
        icon: <Users style={{ color: "#ff9800" }} />,
        description: "Gerenciar contatos",
        path: "/contacts",
      });
    }

    if (showSchedules) {
      userItems.push({
        id: "schedules",
        label: "Agendamentos",
        icon: <Calendar style={{ color: "#f44336" }} />,
        description: "Gerenciar agendamentos",
        path: "/schedules",
      });
    }

    userItems.push({
      id: "tags",
      label: "Tags",
      icon: <Tag style={{ color: "#e91e63" }} />,
      description: "Gerenciar tags",
      path: "/tags",
    });

    // Relatório de Origem de Contatos
    userItems.push({
      id: "contact-origin-report",
      label: "Origem de Contatos",
      icon: <PieChart style={{ color: "#00bcd4" }} />,
      description: "Rastreamento UTM e origem",
      path: "/contact-origin-report",
    });

    // Configuração do Smart Link
    userItems.push({
      id: "smart-link-config",
      label: "Smart Link Config",
      icon: <Link style={{ color: "#2196f3" }} />,
      description: "Configurar link único inteligente",
      path: "/smart-link-config",
    });

    if (showInternalChat) {
      userItems.push({
        id: "chats",
        label: "Chats Internos",
        icon: <MessageCircle style={{ color: "#4caf50" }} />,
        description: "Conversas internas",
        path: "/chats",
      });
    }

    // Central de Ajuda - sempre visível
    userItems.push({
      id: "help",
      label: "Central de Ajuda",
      icon: <HelpCircle style={{ color: "#2196f3" }} />,
      description: "Documentação e tutoriais",
      path: "/help",
    });

    // Campanhas e Marketing
    if (user?.showCampaign === "enabled" && showCampaigns) {
      userItems.push(
        {
          id: "campaigns",
          label: "Campanhas",
          icon: <Megaphone style={{ color: "#ffeb3b" }} />,
          description: "Campanhas de marketing",
          path: "/campaigns",
        },
        {
          id: "contact-lists",
          label: "Listas de Contatos",
          icon: <ListIcon style={{ color: "#ffc107" }} />,
          description: "Listas de contatos",
          path: "/contact-lists",
        }
      );
    }

    // Flowbuilder
    if (user.showFlow === "enabled") {
      userItems.push(
        {
          id: "phrase-lists",
          label: "Fluxo de Campanha",
          icon: <Calendar style={{ color: "#9c27b0" }} />,
          description: "Fluxos de campanha",
          path: "/phrase-lists",
        },
        {
          id: "flowbuilders",
          label: "Fluxo de Conversa",
          icon: <Workflow style={{ color: "#e91e63" }} />,
          description: "Construtor de fluxos",
          path: "/flowbuilders",
        }
      );
    }

    // Landing Pages e Grupos
    if (showLandingPages) {
      userItems.push({
        id: "landing-pages",
        label: "Landing Pages",
        icon: <LandPlot style={{ color: "#ff5722" }} />,
        description: "Páginas de destino",
        path: "/landing-pages",
      });
    }

    if (showGroups) {
      userItems.push({
        id: "groups",
        label: "Grupos",
        icon: <Group style={{ color: "#009688" }} />,
        description: "Gerenciar grupos",
        path: "/groups",
      });
    }

    if (showAssistants) {
      userItems.push({
        id: "assistants",
        label: "Assistentes IA",
        icon: <Bot style={{ color: "#3f51b5" }} />,
        description: "Assistentes virtuais",
        path: "/assistants",
      });
    }

    if (showSDR && user.profile === "admin") {
      userItems.push({
        id: "sdr",
        label: "IA SDR",
        icon: <Bot style={{ color: "#00bcd4" }} />,
        description: "Sistema de prospecção automatizada",
        path: "/sdr",
      });
    }

    if ((user.profile === "user" && user.allowConnections === "enabled") || user.profile === "admin") {
      if (user.super) {
        admin.push({
          id: "announcements",
          label: "Anúncios",
          icon: <Megaphone style={{ color: "#f44336" }} />,
          description: "Gerenciar anúncios",
          path: "/announcements",
        });
      }

      if (showExternalApi) {
        admin.push({
          id: "messages-api",
          label: "API de Mensagens",
          icon: <Zap style={{ color: "#673ab7" }} />,
          description: "API para mensagens",
          path: "/messages-api",
        });
      }

      admin.push({
        id: "users",
        label: "Usuários",
        icon: <Users style={{ color: "#3f51b5" }} />,
        description: "Gerenciar usuários",
        path: "/users",
      });

      admin.push({
        id: "queues",
        label: "Filas",
        icon: <ListIcon style={{ color: "#2196f3" }} />,
        description: "Filas de atendimento",
        path: "/queues",
      });

      if (showOpenAi) {
        admin.push({
          id: "prompts",
          label: "Prompts IA",
          icon: <Bot style={{ color: "#00bcd4" }} />,
          description: "Prompts de IA",
          path: "/prompts",
        });
      }

      if (showIntegrations) {
        admin.push({
          id: "queue-integration",
          label: "Integrações",
          icon: <Layers style={{ color: "#9c27b0" }} />,
          description: "Integrações de filas",
          path: "/queue-integration",
        });
      }

      admin.push({
        id: "connections",
        label: "Conexões",
        icon: <Phone style={{ color: "#9c27b0" }} />,
        description: "Conexões WhatsApp",
        path: "/connections",
      });

      if (user.super) {
        admin.push({
          id: "allConnections",
          label: "Todas Conexões",
          icon: <Globe style={{ color: "#2196f3" }} />,
          description: "Todas as conexões",
          path: "/allConnections",
        });
      }

      // Campanhas Config
      if (user?.showCampaign === "enabled" && showCampaigns) {
        admin.push({
          id: "campaigns-config",
          label: "Config. Campanhas",
          icon: <AdminIcon style={{ color: "#757575" }} />,
          description: "Configurações de campanhas",
          path: "/campaigns-config",
        });
      }

      admin.push({
        id: "financeiro",
        label: "Financeiro",
        icon: <DollarSign style={{ color: "#4caf50" }} />,
        description: "Gestão financeira",
        path: "/financeiro",
      });

      admin.push({
        id: "settings",
        label: "Configurações",
        icon: <AdminIcon style={{ color: "#757575" }} />,
        description: "Configurações do sistema",
        path: "/settings",
      });
    }

    return { dashboard, user: userItems, admin };
  };

  return getNavigationCategories();
}

function AppBarNavigationMenu() {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [dashboardAnchor, setDashboardAnchor] = useState(null);
  const [userAnchor, setUserAnchor] = useState(null);
  const [adminAnchor, setAdminAnchor] = useState(null);

  const { dashboard, user: userItems, admin } = useNavigationActions();

  if (!user) return null;

  const handleMenuClick = (event, setAnchor) => {
    if (event?.currentTarget && typeof event.currentTarget.blur === "function") {
      event.currentTarget.blur();
    }
    setAnchor(event.currentTarget);
  };

  const handleMenuClose = (setAnchor) => {
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    setAnchor(null);
  };

  const handleItemClick = (path, setAnchor) => {
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    setAnchor(null);
    history.push(path);
  };

  const renderMenu = (items, anchor, setAnchor, title) => (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={() => handleMenuClose(setAnchor)}
      classes={{ paper: classes.menuPaper }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      getContentAnchorEl={null}
      autoFocus={false}
      disableAutoFocusItem={true}
      disableRestoreFocus={true}
      transitionDuration={0}
      MenuListProps={{
        style: {
          padding: 0,
        },
      }}
    >
      <Box className={classes.categoryHeader}>
        <Typography variant="subtitle2" fontWeight="bold" noWrap>
          {title}
        </Typography>
      </Box>
      <Divider />
      {items.map((item, index) => (
        <MenuItem
          key={item.id}
          onClick={() => handleItemClick(item.path, setAnchor)}
          className={classes.menuItem}
          divider={index < items.length - 1}
        >
          <ListItemIcon className={classes.iconWrapper}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            secondary={item.description}
            className={classes.menuItemText}
          />
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <Box className={classes.menuContainer}>
      {/* Dashboard & Gestão */}
      {dashboard.length > 0 && (
        <>
          <Tooltip title="Dashboard & Gestão" placement="bottom">
            <IconButton
              className={classes.menuButton}
              onClick={(e) => handleMenuClick(e, setDashboardAnchor)}
              size="medium"
            >
                <DashboardIcon className={classes.menuButtonIcon} />
            </IconButton>
          </Tooltip>
          {renderMenu(dashboard, dashboardAnchor, setDashboardAnchor, "Dashboard & Gestão")}
        </>
      )}

      {/* Itens de Usuário */}
      {userItems.length > 0 && (
        <>
          <Tooltip title="Ferramentas de Trabalho" placement="bottom">
            <IconButton
              className={classes.menuButton}
              onClick={(e) => handleMenuClick(e, setUserAnchor)}
              size="medium"
            >
                <PersonIcon className={classes.menuButtonIcon} />
            </IconButton>
          </Tooltip>
          {renderMenu(userItems, userAnchor, setUserAnchor, "Ferramentas de Trabalho")}
        </>
      )}

      {/* Administração */}
      {admin.length > 0 && (
        <>
          <Tooltip title="Administração" placement="bottom">
            <IconButton
              className={classes.menuButton}
              onClick={(e) => handleMenuClick(e, setAdminAnchor)}
              size="medium"
            >
                <AdminIcon className={classes.menuButtonIcon} />
            </IconButton>
          </Tooltip>
          {renderMenu(admin, adminAnchor, setAdminAnchor, "Administração")}
        </>
      )}
    </Box>
  );
}

export default AppBarNavigationMenu;